import axios from 'axios'

/**
 * API client.
 *
 * The bearer token lives in memory, with a copy in sessionStorage rather than
 * localStorage. sessionStorage is scoped to the tab and cleared when it closes,
 * which suits a portal frequently opened on a shared plant-floor machine — a
 * localStorage token would still be there for whoever sits down next.
 */

const TOKEN_KEY = 'gp.token'
const TENANT_KEY = 'gp.tenant'

let token = sessionStorage.getItem(TOKEN_KEY) || null
let tenantCode = localStorage.getItem(TENANT_KEY) || null

// Called on 401 so the app can drop to the login screen from anywhere,
// including a background poll, without every caller handling it.
let onUnauthorized = () => {}

export function setUnauthorizedHandler(handler) {
  onUnauthorized = handler
}

export function setToken(value) {
  token = value
  if (value) sessionStorage.setItem(TOKEN_KEY, value)
  else sessionStorage.removeItem(TOKEN_KEY)
}

export function getToken() {
  return token
}

/**
 * The tenant code persists in localStorage, unlike the token.
 *
 * It is not a secret — it only says which organisation's login page to show —
 * and remembering it across sessions saves every employee from typing their
 * company code each morning.
 */
export function setTenantCode(value) {
  tenantCode = value
  if (value) localStorage.setItem(TENANT_KEY, value)
  else localStorage.removeItem(TENANT_KEY)
}

export function getTenantCode() {
  return tenantCode
}

// Relative by default — same-origin in both dev (via vite.config.js's proxy)
// and production (the API lives at /api on the same domain as the SPA; see
// DEPLOYMENT.md). Overridable per environment through VITE_API_BASE_URL,
// e.g. a staging deploy that points at a different host.
const client = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  timeout: 30000,
  headers: { Accept: 'application/json' },
})

client.interceptors.request.use((config) => {
  if (token) config.headers.Authorization = `Bearer ${token}`

  // Unauthenticated endpoints (anonymous whistleblower submission, the survey
  // token page) need the tenant identified some other way.
  if (tenantCode) config.headers['X-Tenant'] = tenantCode

  return config
})

/**
 * Why the last session ended, kept in sessionStorage rather than in React
 * state.
 *
 * A 401 frequently arrives on a hard page load — someone refreshes, or opens a
 * bookmark with a token that has since expired. React state is gone at that
 * point, so an in-memory flag would be lost precisely when the explanation is
 * most needed. sessionStorage survives the reload and dies with the tab, which
 * is exactly the lifetime this wants.
 */
const ENDED_KEY = 'gp.sessionEnded'

export function takeSessionEndedReason() {
  try {
    const reason = sessionStorage.getItem(ENDED_KEY)
    // Read once. Leaving it set would show the notice again on the next
    // deliberate visit to the sign-in page.
    sessionStorage.removeItem(ENDED_KEY)

    return reason
  } catch {
    return null
  }
}

export function clearSessionEndedReason() {
  try {
    sessionStorage.removeItem(ENDED_KEY)
  } catch {
    // Private-mode browsers can throw on storage access. Losing the notice is
    // cosmetic; throwing here would break sign-in.
  }
}

client.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status

    if (status === 401) {
      /*
       * Was a token actually being presented?
       *
       * This distinction is the whole difference between "your session ended"
       * and silence. A 401 with no token is either an anonymous caller hitting
       * a guarded route, or — more commonly — a request that was already in
       * flight when the user deliberately signed out. Reporting either as an
       * expired session tells someone who just clicked "Sign out" that
       * something went wrong.
       */
      const hadToken = Boolean(token)

      if (hadToken) {
        try {
          sessionStorage.setItem(ENDED_KEY, 'expired')
        } catch {
          // See clearSessionEndedReason().
        }
      }

      setToken(null)
      onUnauthorized(hadToken)
    }

    return Promise.reject(normalise(error))
  },
)

/**
 * Turn any failure into one predictable shape.
 *
 * Screens should render `error.message` and be done with it. Without this every
 * catch block ends up reaching into `err.response.data.message` and falling
 * over on the network errors that have no response at all — which are exactly
 * the ones that happen on a mine site.
 */
function normalise(error) {
  const response = error.response

  if (!response) {
    return {
      status: 0,
      message: navigator.onLine
        ? 'Could not reach the server. Please try again.'
        : 'You appear to be offline. Check your connection and try again.',
      fields: {},
      offline: !navigator.onLine,
      original: error,
    }
  }

  const data = response.data || {}

  return {
    status: response.status,
    message: data.message || defaultMessage(response.status),
    // Field-level validation errors, keyed by field name, so a form can put
    // each message beside the input it belongs to.
    fields: data.errors && !Array.isArray(data.errors) ? data.errors : {},
    original: error,
  }
}

function defaultMessage(status) {
  switch (status) {
    case 400: return 'That request could not be processed.'
    case 401: return 'Your session has ended. Please sign in again.'
    case 403: return 'You do not have permission to do that.'
    case 404: return 'Not found.'
    case 409: return 'That conflicts with something that already exists.'
    case 422: return 'Please check the highlighted fields.'
    case 429: return 'Too many attempts. Please wait a moment and try again.'
    case 500: return 'Something went wrong at our end. Please try again.'
    default:  return 'Something went wrong. Please try again.'
  }
}

/** Unwraps the `{ success, data }` envelope so callers deal in plain data. */
async function unwrap(promise) {
  const response = await promise
  return response.data?.data !== undefined ? response.data.data : response.data
}

export const api = {
  get:    (url, config) => unwrap(client.get(url, config)),
  post:   (url, body, config) => unwrap(client.post(url, body, config)),
  put:    (url, body, config) => unwrap(client.put(url, body, config)),
  delete: (url, config) => unwrap(client.delete(url, config)),

  /** Full response, for endpoints whose `meta` (pagination) the caller needs. */
  raw: {
    get: (url, config) => client.get(url, config).then((r) => r.data),
  },

  /** File downloads — exports come back as a blob, not JSON. */
  async download(url, filename, config = {}) {
    const response = await client.get(url, { ...config, responseType: 'blob' })

    const objectUrl = URL.createObjectURL(response.data)
    const link = document.createElement('a')

    link.href = objectUrl
    link.download = filename ?? deriveFilename(response) ?? 'download'
    document.body.appendChild(link)
    link.click()
    link.remove()

    // Revoked on the next tick: revoking synchronously can cancel the download
    // in some browsers before it has started.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
  },

  upload(url, formData, onProgress) {
    return unwrap(
      client.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // Imports of several thousand employees legitimately take a while.
        timeout: 180000,
        onUploadProgress: (event) => {
          if (onProgress && event.total) {
            onProgress(Math.round((event.loaded / event.total) * 100))
          }
        },
      }),
    )
  },
}

function deriveFilename(response) {
  const disposition = response.headers['content-disposition']
  if (!disposition) return null

  const match = /filename="?([^"]+)"?/.exec(disposition)
  return match ? match[1] : null
}

export default client
